// client/src/hooks/useOnlinePresence.js

import { useState, useEffect, useCallback, useRef } from 'react'
import ChatService from '../services/ChatService'
import ApiChat from '../api/ApiChat'

/**
 * Custom hook for managing online presence
 * @param {Object} activeChannel - Currently active channel
 * @param {boolean} chatConnected - Whether chat service is connected
 * @returns {Object} Presence state and methods
 */
export default function useOnlinePresence(activeChannel, chatConnected) {
  // channelId -> Map<playerId, user>
  const [onlineUsers, setOnlineUsers] = useState(new Map())
  const loadedChannelsRef = useRef(new Set()) // Track which channels we've loaded

  // Handle incoming presence events
  const handlePresenceEvent = useCallback((data) => {
    const { playerId, playerName, isOnline, connectedAt, lastSeen } = data

    // Update presence for all channels this user might be in
    setOnlineUsers((prevOnline) => {
      const newOnline = new Map(prevOnline)

      // Update all channels that might have this user
      for (const [channelId, channelUsers] of newOnline.entries()) {
        const updatedUsers = new Map(channelUsers)

        if (isOnline) {
          updatedUsers.set(playerId, {
            playerId,
            playerName,
            isOnline: true,
            connectedAt: connectedAt ? new Date(connectedAt) : new Date(),
            lastSeen: null,
          })
        } else {
          // Mark offline with lastSeen
          const existing = updatedUsers.get(playerId)
          if (existing) {
            updatedUsers.set(playerId, {
              ...existing,
              isOnline: false,
              lastSeen: lastSeen ? new Date(lastSeen) : new Date(),
            })
          }
        }

        newOnline.set(channelId, updatedUsers)
      }

      return newOnline
    })
  }, [])

  // Set up presence event listener
  useEffect(() => {
    if (!chatConnected) return

    const unsubscribe = ChatService.onPresence(handlePresenceEvent)

    return unsubscribe
  }, [chatConnected, handlePresenceEvent])

  // Load initial online users for active channel
  useEffect(() => {
    if (!activeChannel || !chatConnected) return
    if (loadedChannelsRef.current.has(activeChannel.id)) return

    const loadOnlineUsers = async () => {
      try {
        const response = await ApiChat.getOnlineUsers(activeChannel.id)
        const usersMap = new Map(
          (response.onlineUsers || []).map((user) => [
            user.playerId,
            {
              playerId: user.playerId,
              playerName: user.playerName,
              isOnline: user.isOnline,
              connectedAt: user.connectedAt
                ? new Date(user.connectedAt)
                : new Date(),
              lastSeen: user.lastSeen ? new Date(user.lastSeen) : null,
            },
          ])
        )

        setOnlineUsers((prevOnline) => {
          const newOnline = new Map(prevOnline)
          newOnline.set(activeChannel.id, usersMap)
          return newOnline
        })

        loadedChannelsRef.current.add(activeChannel.id)
      } catch (error) {
        console.error('Failed to load online users:', error)
      }
    }

    loadOnlineUsers()
  }, [activeChannel, chatConnected])

  // Get online users for a specific channel
  const getOnlineUsersForChannel = useCallback(
    (channelId) => {
      if (!channelId || !onlineUsers.has(channelId)) return []
      const users = Array.from(onlineUsers.get(channelId).values())
      return users.sort((a, b) => {
        // Sort by online status first, then by name
        if (a.isOnline && !b.isOnline) return -1
        if (!a.isOnline && b.isOnline) return 1
        return a.playerName.localeCompare(b.playerName)
      })
    },
    [onlineUsers]
  )

  // Get count of online users for a channel
  const getOnlineCountForChannel = useCallback(
    (channelId) => {
      if (!channelId || !onlineUsers.has(channelId)) return 0
      const users = Array.from(onlineUsers.get(channelId).values())
      return users.filter((user) => user.isOnline).length
    },
    [onlineUsers]
  )

  return {
    getOnlineUsersForChannel,
    getOnlineCountForChannel,
  }
}
